import classNames from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import _startCase from 'lodash/startCase';
import type { CSSProperties, ReactNode } from 'react';
import { Icon, type SemanticCOLORS } from 'semantic-ui-react';

import { SEMANTIC_COLORS } from '@models/semantic-ui.const';
import { darkReaderIgnoreInlineStyleClass } from '@components/dark-mode/dark-mode-provider';

import s from './stat-breakdown.scss';

const parseColor = (color: SemanticCOLORS | CSSProperties['color']) => {
  if (SEMANTIC_COLORS.includes(color as SemanticCOLORS)) {
    return `var(--sui-color-${color})`;
  }

  return color;
};

export interface StatBreakdownItem {
  /**
   * Color of the leading dot indicator. Accepts a SUI semantic color
   * or any CSS color value (including CSS custom properties).
   */
  color?: SemanticCOLORS | CSSProperties['color'];
  /**
   * Optional sub-label rendered below the main label in a muted style.
   */
  description?: ReactNode;
  /**
   * Stable identifier — used as the React key for the row, and as the
   * fallback label (start-cased) when `label` is not provided.
   */
  key: string;
  /**
   * Bold primary label for the row. Defaults to a start-cased form of
   * `key` when omitted.
   */
  label?: string;
  /**
   * Numeric value rendered right-aligned, formatted with `toLocaleString()`.
   */
  value: number;
}

export interface StatBreakdownProps {
  /**
   * Extra class applied to the root `<dl>`. Use this to layer on layout
   * overrides — e.g. setting `--stat-breakdown-min-width` to widen the
   * popup, or applying spacing tweaks for a specific surface.
   */
  className?: string;
  /**
   * When true, items with a value of zero are filtered out before rendering.
   * @default false
   */
  hideZero?: boolean;
  items: StatBreakdownItem[];
}

/**
 * Renders a vertical breakdown list — each row showing a colored dot, a bold
 * label (with an optional sub-label below), and a right-aligned numeric value.
 *
 * Designed to be used as the content of a hover popup that summarizes a
 * compact visualization (severity pills, gauge bars, etc.). Returns `null`
 * when no items remain after filtering.
 */
export const StatBreakdown = ({
  className,
  hideZero = false,
  items,
}: StatBreakdownProps) => {
  useStyles(s);

  const visibleItems = hideZero
    ? items.filter(item => item.value !== 0)
    : items;

  if (visibleItems.length === 0) return null;

  return (
    <dl className={classNames(s.list, className)}>
      {visibleItems.map(item => (
        <div className={s.item} key={item.key}>
          <dt className={s.term}>
            <Icon
              aria-hidden="true"
              className={classNames(s.dot, darkReaderIgnoreInlineStyleClass)}
              name="circle"
              style={{ color: parseColor(item.color) }}
            />
            <span className={s.label}>
              {item.label ?? _startCase(item.key)}
            </span>
            {item.description ? (
              <span className={s.description}>{item.description}</span>
            ) : null}
          </dt>
          <dd className={s.value}>{item.value.toLocaleString()}</dd>
        </div>
      ))}
    </dl>
  );
};

export default StatBreakdown;
